// lifetime-sections-v0 reader (docs/contracts/lifetime-sections-v0.md): the app-read sibling of a lifetime-sim-v0
// sequence, for the Lifetime scene's model track. It carries closed 2D sections in RAS mm: a model's boundary states
// (LIFE-014's Newton fold: one point count, linear in matching material nodes for display) and the atlas's own sections
// at its sampled weeks (all loops, ragged: no point correspondence between weeks, so the nearest sampled week is shown
// as itself), each block on its own time axis over the scene's one clock. Never sent to the viewport: the engine morphs
// the atlas, the app draws the section overlay.

import type { SimFiles } from './files.ts';
import type { SectionBlockHeader, SectionsHeader } from './types.ts';

/** One or more closed polylines at a time: each loop is xyz triples, RAS mm, the last point joining the first. */
export interface Outline {
  loops: Float32Array[];
  /** The age shown with it, in the block's age unit (gestational weeks). */
  age: number;
  /** The frame's own time, s (for a sampled block, the sampled week's; for an interpolated one, the time asked for). */
  t: number;
  /** The section's sagittal plane, RAS x in mm; null where the block states none. */
  planeX: number | null;
  /** Whether the outline is a stored frame (a sampled week, a saved state) or interpolated between two. */
  exact: boolean;
}

export interface SectionsMetrics {
  model: Record<string, unknown>[];
  atlas: Record<string, unknown>[];
  life015?: Record<string, unknown>;
  units?: Record<string, string>;
}

const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

function axis(block: SectionBlockHeader | undefined, what: string, where: string): { times: Float64Array; ages: number[] } {
  const values = block?.time?.values;
  if (!Array.isArray(values) || !values.length || !values.every(num)) throw new Error(`${where}: ${what} without time.values`);
  for (let i = 1; i < values.length; i++) if (!(values[i]! > values[i - 1]!)) throw new Error(`${where}: ${what} time.values must be strictly increasing`);
  const ages = block?.age?.values;
  if (!Array.isArray(ages) || ages.length !== values.length || !ages.every(num)) throw new Error(`${where}: ${what} age.values must match time.values`);
  return { times: Float64Array.from(values), ages: [...ages] };
}

/** The index of the sample nearest `t` (ties go to the later one, as the contract asks), clamped to the ends. */
export function nearestIndex(times: ArrayLike<number>, t: number): number {
  let best = 0;
  for (let i = 1; i < times.length; i++) if (Math.abs(times[i]! - t) <= Math.abs(times[best]! - t)) best = i;
  return best;
}

/** The frames around `t` and the weight of the later one, clamped to the ends. */
export function bracket(times: ArrayLike<number>, t: number): { i: number; j: number; w: number } {
  const n = times.length;
  if (n === 1 || t <= times[0]!) return { i: 0, j: 0, w: 0 };
  if (t >= times[n - 1]!) return { i: n - 1, j: n - 1, w: 0 };
  let j = 1;
  while (j < n - 1 && times[j]! < t) j++;
  return { i: j - 1, j, w: (t - times[j - 1]!) / (times[j]! - times[j - 1]!) };
}

export class SectionsSim {
  private metricsP: Promise<SectionsMetrics | null> | null = null;

  private constructor(
    readonly files: SimFiles,
    readonly header: SectionsHeader,
    /** The model's saved states: `points` xyz triples a frame, frame-major. */
    readonly model: { positions: Float32Array; points: number; times: Float64Array; ages: number[]; growth: number[] | null; planeX: number[] | null; name: string },
    /** The atlas's own sections at its sampled weeks: every loop, delimited by offsets into `points` (xyz triples). */
    readonly atlas: { points: Float32Array; loopOffsets: Uint32Array; frameLoopOffsets: Uint32Array; times: Float64Array; ages: number[]; planeX: number[] | null },
  ) {}

  static async open(files: SimFiles, raw: SectionsHeader): Promise<SectionsSim> {
    const where = `sims/${raw.id}`;
    const m = raw.sections?.model;
    const a = raw.sections?.atlas;
    if (!m?.positions) throw new Error(`${where}: a lifetime-sections-v0 run without sections.model.positions`);
    if (!a?.positions || !a.loop_offsets || !a.frame_loop_offsets) throw new Error(`${where}: a lifetime-sections-v0 run without the atlas's points and offsets`);
    const mAxis = axis(m, 'sections.model', where);
    const aAxis = axis(a, 'sections.atlas', where);
    const [mPos, aPos, loopOffsets, frameLoopOffsets] = await Promise.all([files.array(m.positions), files.array(a.positions), files.array(a.loop_offsets), files.array(a.frame_loop_offsets)]);
    if (!(mPos instanceof Float32Array) || !(aPos instanceof Float32Array)) throw new Error(`${where}: section positions must be <f4`);
    if (!(loopOffsets instanceof Uint32Array) || !(frameLoopOffsets instanceof Uint32Array)) throw new Error(`${where}: offsets must be <u4`);
    const frames = mAxis.times.length;
    const points = m.points ?? (m.positions.shape?.[1] as number | undefined) ?? 0;
    if (!(points > 2) || mPos.length !== frames * points * 3) throw new Error(`${where}: sections.model.positions has ${mPos.length} values, not ${frames} frames × ${points} points × 3`);
    if (frameLoopOffsets.length !== aAxis.times.length + 1) throw new Error(`${where}: frame_loop_offsets has ${frameLoopOffsets.length} entries for ${aAxis.times.length} sampled weeks`);
    if (frameLoopOffsets[0] !== 0 || frameLoopOffsets[frameLoopOffsets.length - 1] !== loopOffsets.length - 1) throw new Error(`${where}: frame_loop_offsets must run from 0 to the loop count`);
    for (let i = 1; i < frameLoopOffsets.length; i++) if (frameLoopOffsets[i]! < frameLoopOffsets[i - 1]!) throw new Error(`${where}: frame_loop_offsets must not decrease`);
    if (loopOffsets[0] !== 0 || loopOffsets[loopOffsets.length - 1]! * 3 !== aPos.length) throw new Error(`${where}: loop_offsets must run from 0 to the point count (${aPos.length / 3})`);
    for (let i = 1; i < loopOffsets.length; i++) if (loopOffsets[i]! < loopOffsets[i - 1]! + 3) throw new Error(`${where}: every atlas loop needs at least three points`);
    const planes = (b: SectionBlockHeader, n: number): number[] | null => (Array.isArray(b.plane?.x_mm) && b.plane.x_mm.length === n && b.plane.x_mm.every(num) ? [...b.plane.x_mm] : null);
    const growth = Array.isArray(raw.growth?.values) && raw.growth.values.length === frames && raw.growth.values.every(num) ? [...raw.growth.values] : null;
    return new SectionsSim(
      files,
      raw,
      { positions: mPos, points, times: mAxis.times, ages: mAxis.ages, growth, planeX: planes(m, frames), name: raw.model?.name ?? 'Model' },
      { points: aPos, loopOffsets, frameLoopOffsets, times: aAxis.times, ages: aAxis.ages, planeX: planes(a, aAxis.times.length) },
    );
  }

  /** The model's boundary at `t`: a saved state at its own time, else the two around it blended point by point (display only). */
  modelAt(t: number): Outline & { growth: number | null } {
    const { times, ages, points, positions } = this.model;
    const { i, j, w } = bracket(times, t);
    const n = points * 3;
    const out = new Float32Array(n);
    const a = positions.subarray(i * n, (i + 1) * n);
    if (i === j || w === 0) out.set(a);
    else {
      const b = positions.subarray(j * n, (j + 1) * n);
      for (let k = 0; k < n; k++) out[k] = a[k]! + w * (b[k]! - a[k]!);
    }
    const mix = (xs: number[] | null) => (xs ? xs[i]! + w * (xs[j]! - xs[i]!) : null);
    return { loops: [out], age: mix(ages)!, t: i === j ? times[i]! : t, planeX: mix(this.model.planeX), exact: i === j || w === 0, growth: mix(this.model.growth) };
  }

  /** The atlas's own section at the sampled week nearest `t` (ties later): its loops as stored, its age and plane. */
  atlasAt(t: number): Outline & { index: number } {
    const k = nearestIndex(this.atlas.times, t);
    const { frameLoopOffsets: F, loopOffsets: L, points } = this.atlas;
    const loops: Float32Array[] = [];
    for (let l = F[k]!; l < F[k + 1]!; l++) loops.push(points.subarray(L[l]! * 3, L[l + 1]! * 3));
    return { loops, age: this.atlas.ages[k]!, t: this.atlas.times[k]!, planeX: this.atlas.planeX?.[k] ?? null, exact: true, index: k };
  }

  /** The two blocks' extent in the sagittal plane, RAS y and z, mm: [[y0, z0], [y1, z1]] over every frame. */
  get boundsYZ(): [[number, number], [number, number]] {
    let y0 = Infinity, z0 = Infinity, y1 = -Infinity, z1 = -Infinity;
    for (const arr of [this.model.positions, this.atlas.points])
      for (let k = 0; k < arr.length; k += 3) {
        const y = arr[k + 1]!, z = arr[k + 2]!;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
        if (z < z0) z0 = z;
        if (z > z1) z1 = z;
      }
    return [[y0, z0], [y1, z1]];
  }

  /** metrics.json: the model's rows (growth, perimeter, fold index, section area…) and the atlas's, with LIFE-015's record. */
  metrics(): Promise<SectionsMetrics | null> {
    return (this.metricsP ??= this.files.maybeJson<Record<string, unknown>>(this.header.metrics ?? 'metrics.json').then((m) => {
      if (!m || typeof m !== 'object') return null;
      const rows = (v: unknown) => (Array.isArray(v) ? v.filter((r): r is Record<string, unknown> => !!r && typeof r === 'object') : []);
      return { model: rows(m.model), atlas: rows(m.atlas), ...(m.life015 && typeof m.life015 === 'object' ? { life015: m.life015 as Record<string, unknown> } : {}), ...(m.units && typeof m.units === 'object' ? { units: m.units as Record<string, string> } : {}) };
    }));
  }
}
